import { apiGetUserGuessOrder } from "@/api/api";
import { get } from "@/utils/utils";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { toNumber } from "../positions-panel-components/utils";

/**
 * 挂单数据获取和处理 Hook
 *
 * 功能：
 * - 从 API 获取用户的挂单数据
 * - 按价格、类型合并相同的订单
 * - 按下单时间倒序排列
 *
 * @param guessNo - 竞猜编号
 * @param userId - 用户ID
 * @param enabled - 是否启用数据获取
 * @returns { ordersItems } - 处理后的挂单列表
 */
export function useOrdersData( guessNo: string, userId: number | null, enabled: boolean ) {
    const { data: ordersData, isLoading } = useQuery( {
        queryKey: [ "guess-open-orders", guessNo, userId ],
        queryFn: async () => {
            const [ isOk, res ] = await apiGetUserGuessOrder( {
                guessNo: guessNo || "",
            } );
            if ( isOk ) return get( res, "data", {} );
            throw new Error( get( res, "msg", "Failed to load orders" ) );
        },
        enabled: !!guessNo && enabled && !!userId,
        refetchInterval: 10_000,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    } );

    const ordersItems = useMemo( () => {
        const list = get( ordersData, "orders", null );
        const items = ( Array.isArray( list ) ? list : [] ).filter( ( order: any ) => {
            const orderGuessNo = get( order, "guessNo", "" );
            const fallbackGuessNo = get( ordersData, "guessNo", "" );
            const resolvedGuessNo = orderGuessNo || fallbackGuessNo;
            return !guessNo || resolvedGuessNo === guessNo;
        } );
        const grouped = new Map<string, any>();

        items.forEach( ( order: any ) => {
            const betType = toNumber( get( order, "betType", get( order, "guessType", 1 ) ), 1 );
            const direction = toNumber( get( order, "direction", 1 ), 1 );
            const price = toNumber( get( order, "price", 0 ) );
            const key = `${betType}-${direction}-${price}`;
            const totalQuantity = toNumber( get( order, "totalQuantity", get( order, "quantity", 0 ) ) );
            const dealtQuantity = toNumber(
                get( order, "totalDealtQuantity", get( order, "dealtQuantity", get( order, "filledQuantity", 0 ) ) ),
            );
            const orderTime = toNumber(
                get( order, "orderTime", get( order, "createTime", get( order, "lastOrderTime", 0 ) ) ),
            );

            if ( !grouped.has( key ) ) {
                grouped.set( key, {
                    ...order,
                    title: get( ordersData, "title", get( order, "title", "" ) ),
                    coverImageUrl: get( ordersData, "coverImageUrl", get( order, "coverImageUrl", "" ) ),
                    betType,
                    direction,
                    price,
                    totalQuantity,
                    totalDealtQuantity: dealtQuantity,
                    lastOrderTime: orderTime,
                    totalAmount: toNumber( get( order, "totalAmount", price * totalQuantity ) ),
                } );
            } else {
                const prev = grouped.get( key );
                grouped.set( key, {
                    ...prev,
                    totalQuantity: prev.totalQuantity + totalQuantity,
                    totalDealtQuantity: prev.totalDealtQuantity + dealtQuantity,
                    lastOrderTime: Math.max( prev.lastOrderTime, orderTime ),
                    totalAmount: toNumber( get( order, "totalAmount", prev.totalAmount + price * totalQuantity ) ),
                } );
            }
        } );

        return Array.from( grouped.values() ).sort( ( a, b ) => ( b.lastOrderTime || 0 ) - ( a.lastOrderTime || 0 ) );
    }, [ ordersData, guessNo ] );

    return { ordersItems, isLoading };
}
